import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import { fmtMoney } from "@/lib/finance";

const GRID = "rgba(23,35,31,0.10)";
const AXIS = "rgba(23,35,31,0.55)";

const tooltipStyle = {
  backgroundColor: "#ffffff",
  border: "1px solid rgba(23,35,31,0.14)",
  borderRadius: 10,
  boxShadow: "0 8px 18px rgba(23,35,31,0.08)",
  fontSize: 12,
  color: "#17231f",
};

export const NetWorthChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={260}>
    <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
      <defs>
        <linearGradient id="nw" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1f7355" stopOpacity={0.28} />
          <stop offset="100%" stopColor="#1f7355" stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="label" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} />
      <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false}
        tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} width={44} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmtMoney(v), "Net worth"]} />
      <Area type="monotone" dataKey="value" stroke="#1f7355" strokeWidth={2.5} fill="url(#nw)" />
    </AreaChart>
  </ResponsiveContainer>
);

const DONUT_COLORS = ["#1f7355", "rgba(31,115,85,0.55)", "#c68b3c", "rgba(198,139,60,0.55)", "rgba(23,35,31,0.25)"];

export const AllocationDonut = ({ data, height = 240 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <PieChart>
      <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="85%" paddingAngle={2} stroke="none">
        {data.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
      </Pie>
      <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v}`, n]} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#17231f" }} />
    </PieChart>
  </ResponsiveContainer>
);

export const BudgetVsActualBars = ({ data, height = 280 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
      <CartesianGrid stroke={GRID} horizontal={false} />
      <XAxis type="number" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false}
        tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
      <YAxis type="category" dataKey="category" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} width={92} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtMoney(v), n]} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="budget" name="Budget" fill="rgba(31,115,85,0.28)" radius={[0, 6, 6, 0]} maxBarSize={16} />
      <Bar dataKey="spent" name="Spent" fill="#1f7355" radius={[0, 6, 6, 0]} maxBarSize={16} />
    </BarChart>
  </ResponsiveContainer>
);

export const IncomeSpendBars = ({ data }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="label" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} />
      <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false}
        tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} width={44} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtMoney(v), n]} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="income" name="Income" fill="#1f7355" radius={[6, 6, 0, 0]} maxBarSize={40} />
      <Bar dataKey="spending" name="Spending" fill="#c68b3c" radius={[6, 6, 0, 0]} maxBarSize={40} />
    </BarChart>
  </ResponsiveContainer>
);

export const TrendChart = ({ data, name = "Value", color = "#1f7355", money = false }) => (
  <ResponsiveContainer width="100%" height={220}>
    <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
      <defs>
        <linearGradient id={`tg-${name}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.26} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="label" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} />
      <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} width={44} domain={["auto", "auto"]} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [money ? fmtMoney(v) : v, name]} />
      <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} fill={`url(#tg-${name})`} />
    </AreaChart>
  </ResponsiveContainer>
);

export const ScoreDial = ({ value, max = 100, label, size = 160 }) => {
  const pct = Math.max(0, Math.min(1, value / max));
  const r = size / 2 - 14;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct * 0.75);
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }} data-testid="financial-health-score-dial">
      <svg width={size} height={size} className="-rotate-[135deg]">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(23,35,31,0.08)"
          strokeWidth={12} strokeDasharray={circ} strokeDashoffset={circ * 0.25} strokeLinecap="round" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1f7355"
          strokeWidth={12} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <div className="absolute text-center">
        <div className="font-serif-display text-3xl tabular-nums text-[var(--text)]">{Math.round(value)}</div>
        {label && <div className="text-[11px] uppercase tracking-[0.12em] text-[var(--text-muted)]">{label}</div>}
      </div>
    </div>
  );
};
